/**
 * 科技蓝模板 - 首页
 * 风格：清新极简 + 内容优先
 */

import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { getListings, getSiteCategories, getSiteInfo, ListingItem, FrontendCategory } from "@/utils/api";
import TechBlueNavbar from "../components/Navbar";
import TechBlueFooter from "../components/Footer";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function HomePage() {
  const { siteConfig } = useSiteConfig();
  const siteDisplayName = siteConfig?.site_name || siteConfig?.name || "";

  const [listings, setListings] = useState<ListingItem[]>([]);
  const [categories, setCategories] = useState<FrontendCategory[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [searchConfig, setSearchConfig] = useState({ enabled: true, disabledMessage: "" });
  const [showSearchTip, setShowSearchTip] = useState(false);

  useEffect(() => {
    getSiteInfo().then((info) => {
      if (info) {
        if (info.search_enabled !== undefined) {
          setSearchConfig({
            enabled: info.search_enabled !== false,
            disabledMessage: info.search_disabled_message || "搜索功能已关闭",
          });
        }
      }
    });
  }, []);

  useEffect(() => {
    async function load() {
      try { const [l, c] = await Promise.all([getListings({ page: 1, page_size: 8 }), getSiteCategories()]); setListings(l.list || []); setCategories(c || []); }
      catch (e) { console.error(e); }
      finally { setLoading(false); }
    }
    load();
  }, []);

  const handleSearch = useCallback(() => {
    if (!searchConfig.enabled) {
      setShowSearchTip(true);
      setTimeout(() => setShowSearchTip(false), 3000);
      return;
    }
    if (searchQuery.trim()) window.location.href = `/category?search=${encodeURIComponent(searchQuery.trim())}`;
  }, [searchQuery, searchConfig]);

  return (
    <div className="min-h-screen bg-white">
      <TechBlueNavbar />

      {/* Hero */}
      <section className="relative bg-gradient-to-b from-slate-50 to-white pt-16 pb-16 sm:pt-20 sm:pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-cyan-50 text-cyan-700 rounded-full text-xs font-medium mb-6">
            <i className="ri-sparkling-line" /> 发现优质商业服务
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            找到您需要的<span className="text-cyan-600">商业服务</span>
          </h1>
          <p className="text-gray-500 text-lg max-w-xl mx-auto mb-8">
            {siteDisplayName || "专业的商业信息服务平台"}
          </p>
          <div className="max-w-xl mx-auto">
            <div className="flex items-center bg-white rounded-2xl shadow-sm border border-gray-200 focus-within:ring-2 focus-within:ring-cyan-300 focus-within:border-transparent transition-all overflow-hidden">
              <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="搜索感兴趣的服务..." className="flex-1 px-5 py-3.5 text-sm outline-none bg-transparent" />
              <button onClick={handleSearch} className="px-6 py-3.5 bg-cyan-600 text-white hover:bg-cyan-700 transition-colors">
                <i className="ri-search-line text-lg" />
              </button>
            </div>
            {showSearchTip && (
              <p className="mt-2 text-xs text-red-500 flex items-center gap-1">
                <i className="ri-error-warning-line" />
                {searchConfig.disabledMessage || "搜索功能已关闭"}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* 分类云 */}
      {categories.length > 0 && (
        <section className="py-8 border-b border-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide">
              <Link to="/category" className="shrink-0 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-full hover:bg-gray-800 transition-colors">全部</Link>
              {categories.slice(0, 10).map((cat) => (
                <Link key={cat.id} to={`/category?cat=${cat.slug}`}
                  className="shrink-0 px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-full hover:border-cyan-300 hover:text-cyan-600 hover:bg-cyan-50 transition-all">
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 信息列表 */}
      <section className="py-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">最新发布</h2>
            <Link to="/category" className="text-sm text-cyan-600 hover:text-cyan-700 flex items-center gap-1">查看全部 <i className="ri-arrow-right-line" /></Link>
          </div>

          {loading ? (
            <div className="text-center py-16 text-gray-400"><i className="ri-loader-4-line animate-spin text-2xl" /></div>
          ) : listings.length === 0 ? (
            <div className="text-center py-16 text-gray-400"><i className="ri-inbox-line text-4xl mb-2 block" />暂无内容</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {listings.map((item) => (
                <Link key={item.id} to={`/listing/${item.id}`} className="flex items-start gap-5 py-5 hover:bg-gray-50/50 -mx-3 px-3 rounded-lg transition-colors group">
                  {item.images?.[0] ? (
                    <img src={item.images[0]} alt={item.title} className="w-20 h-20 object-cover rounded-xl shrink-0" />
                  ) : (
                    <div className="w-20 h-20 rounded-xl bg-gray-100 flex items-center justify-center shrink-0"><i className="ri-image-line text-gray-300 text-2xl" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-gray-900 group-hover:text-cyan-600 transition-colors line-clamp-1">{item.title}</h3>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">{item.content}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                      {item.category?.name && <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{item.category.name}</span>}
                      <span><i className="ri-time-line mr-0.5" />{item.created_at}</span>
                      <span><i className="ri-eye-line mr-0.5" />{item.view_count}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <TechBlueFooter />
    </div>
  );
}
